/**
 * PublicationChip — one publication rendered inside a DayCell.
 *
 * Detail density switches via `detailLevel`:
 *   - 'high'    (Mode A single-strategy)  → icon + topic title + status
 *   - 'compact' (Mode B cross-plan)       → icon + color dot + truncated title
 *
 * Draggable via HTML5 DnD. The drag payload is just the publication id —
 * the drop handler resolves and rewrites via the service layer.
 */

import React from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import MusicNoteIcon from '@mui/icons-material/MusicNote'; // stand-in for TikTok
import RedditIcon from '@mui/icons-material/Reddit';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { CONTENT_PLAN } from '@test-kit/selectors/content-plan';
import {
  REACTIVE_PUBLICATION_COLOR,
  isReactivePublicationSlot,
  pickPlatformPublicationColor,
} from '@modules/content-plan/utils/platformPublicationColor';
import type { ContentPublication, PlatformId } from '../../types';
import { useCalendarSlotStatus } from './CalendarTaskRegistry';

const PLATFORM_ICONS: Partial<Record<PlatformId, React.ElementType>> = {
  linkedin: LinkedInIcon,
  twitter: TwitterIcon,
  instagram: InstagramIcon,
  tiktok: MusicNoteIcon,
  facebook: FacebookIcon,
  reddit: RedditIcon,
};

export type ChipDetailLevel = 'high' | 'compact';

export interface PublicationChipProps {
  publication: ContentPublication;
  color: string;
  topicTitle?: string;
  detailLevel: ChipDetailLevel;
  onClick?: (pub: ContentPublication) => void;
  onDragStart?: (pub: ContentPublication) => void;
  onDragEnd?: () => void;
}

export const PublicationChip: React.FC<PublicationChipProps> = ({
  publication,
  color,
  topicTitle,
  detailLevel,
  onClick,
  onDragStart,
  onDragEnd,
}) => {
  const Icon = PLATFORM_ICONS[publication.platform] ?? LightbulbOutlinedIcon;
  const hasPost = Boolean(publication.contentPostId);
  const isReactiveSlot = isReactivePublicationSlot(publication);
  const platformColor = pickPlatformPublicationColor(publication.platform);
  const accentColor = isReactiveSlot ? REACTIVE_PUBLICATION_COLOR : platformColor;
  const title = topicTitle || (publication.topicId ? 'Topic (no title)' : isReactiveSlot ? 'Manual topic slot' : 'Empty slot');
  // workflow-golden-tower — render aspect-aware label when this publication
  // belongs to a multi-aspect topic. The persisted aspectKey is null/undefined
  // for single-aspect topics by convention (the 'main' sentinel only lives in
  // pub-id strings, never in this field — see publicationKeys.ts).
  const aspectKey = (publication as { aspectKey?: string }).aspectKey;
  const baseTitle = aspectKey ? `${title} · ${aspectKey}` : title;
  const truncated = detailLevel === 'compact' && baseTitle.length > 16
    ? `${baseTitle.slice(0, 14)}…`
    : baseTitle;
  // H.3 — live task status when this slot has a recently-launched generation.
  const slotStatus = useCalendarSlotStatus(publication.lastTaskId);
  const postStateLabel = isReactiveSlot
    ? 'Manual slot: choose a topic before generating'
    : hasPost ? 'Post generated' : 'Planned (no post yet)';

  return (
    <Tooltip
      arrow
      placement="top"
      title={
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>{title}</Typography>
          {aspectKey && (
            <Typography variant="caption" display="block" color="text.secondary">
              aspect: {aspectKey}
            </Typography>
          )}
          <Typography variant="caption" display="block">
            {publication.platform.toUpperCase()} · {new Date(publication.scheduledAt).toLocaleString()}
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary">
            {postStateLabel}
          </Typography>
        </Box>
      }
    >
      <Box
        data-testid={`${CONTENT_PLAN.CALENDAR_PUBLICATION_CHIP}-${publication.id}`}
        data-publication-id={publication.id}
        data-content-post-id={publication.contentPostId ?? ''}
        data-has-post={hasPost ? 'true' : 'false'}
        data-platform={publication.platform}
        data-source={publication.source}
        data-manual-slot={isReactiveSlot ? 'true' : 'false'}
        data-scheduled-at={publication.scheduledAt}
        data-aspect-key={aspectKey ?? ''}
        draggable
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', publication.id);
          onDragStart?.(publication);
        }}
        onDragEnd={() => onDragEnd?.()}
        onClick={(e) => {
          e.stopPropagation();
          onClick?.(publication);
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          minWidth: 0,
          mb: 0.25,
          px: 0.75,
          py: detailLevel === 'high' ? 0.5 : 0.25,
          borderRadius: 1,
          cursor: 'grab',
          bgcolor: `${accentColor}14`,
          border: `1px ${isReactiveSlot ? 'dashed' : 'solid'} ${isReactiveSlot ? `${accentColor}80` : 'transparent'}`,
          borderLeft: `3px solid ${accentColor}`,
          boxShadow: detailLevel === 'compact' ? `inset -2px 0 0 ${color}` : undefined,
          transition: 'background-color 120ms ease',
          '&:hover': { bgcolor: `${accentColor}2A` },
          '&:active': { cursor: 'grabbing' },
        }}
      >
        {detailLevel === 'compact' && (
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: platformColor, flexShrink: 0 }} />
        )}
        <Icon sx={{ fontSize: detailLevel === 'high' ? 16 : 14, color: platformColor }} />
        <Typography
          variant="caption"
          sx={{
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            fontSize: detailLevel === 'high' ? 12 : 11,
          }}
        >
          {truncated}
        </Typography>
        {/* H.3 — live status badge when a generation is running/done/error.
            Hides when slotStatus='idle' (no recent task or task was idle). */}
        {slotStatus === 'generating' && (
          <HourglassEmptyIcon
            data-testid={`${CONTENT_PLAN.CALENDAR_PUBLICATION_CHIP}-generating`}
            sx={{ fontSize: 14, color: 'info.main', animation: 'spin 2s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }}
          />
        )}
        {slotStatus === 'error' && (
          <ErrorOutlineIcon
            data-testid={`${CONTENT_PLAN.CALENDAR_PUBLICATION_CHIP}-error`}
            sx={{ fontSize: 14, color: 'error.main' }}
          />
        )}
        {detailLevel === 'high' && (slotStatus === 'done' || (slotStatus === 'idle' && hasPost)) && (
          <CheckCircleOutlineIcon sx={{ fontSize: 14, color: 'success.main' }} />
        )}
      </Box>
    </Tooltip>
  );
};
